/**
 * /account — Hub de identidad de Taxor (panel de Tax Account).
 *
 * Replica el patrón de Lex Account: layout sin sidebar, topbar simple
 * (logo + email + logout), contenido centrado max-w-5xl con welcome
 * + cards de "Administrar cuenta" + accesos a otras zonas.
 *
 * No es el dashboard de OPERACIÓN (eso es /). Es el dashboard de
 * IDENTIDAD: perfil, empresas, equipo, roles, sesiones, privacidad.
 */

import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Building2,
  ChevronRight,
  Cookie,
  ExternalLink,
  FileLock2,
  KeyRound,
  Loader2,
  LogOut,
  Monitor,
  ShieldCheck,
  User as UserIcon,
  UserPlus,
  Users,
} from 'lucide-react'

import { useCurrentUser } from '@/hooks/api/use-current-user'
import { useLogout } from '@/hooks/use-logout'
import { ROUTES, APP } from '@/lib/constants'
import { ThemeToggle } from '@/components/ui/theme-toggle'

// ─── Cards config ─────────────────────────────────────────────────

interface AccountCard {
  title: string
  description: string
  icon: typeof UserIcon
  to: string
  variant: 'brand' | 'success' | 'warning' | 'info' | 'amber' | 'rose'
}

const PROFILE_CARDS: AccountCard[] = [
  {
    title: 'Mis datos personales',
    description: 'Nombre, foto, correo y teléfono.',
    icon: UserIcon,
    to: ROUTES.PROFILE + '?tab=data',
    variant: 'brand',
  },
  {
    title: 'Mis empresas',
    description: 'Empresas a las que perteneces y tu rol en cada una.',
    icon: Building2,
    to: ROUTES.PROFILE + '?tab=organizations',
    variant: 'amber',
  },
  {
    title: 'Cambiar empresa',
    description: 'Cambia con qué empresa vas a operar en esta sesión.',
    icon: Building2,
    to: ROUTES.SELECT_ORG,
    variant: 'info',
  },
  {
    title: 'Contraseña',
    description: 'Configura o actualiza tu contraseña.',
    icon: KeyRound,
    to: ROUTES.PROFILE + '?tab=password',
    variant: 'rose',
  },
  {
    title: 'Autenticación 2FA',
    description: 'Códigos TOTP, códigos de respaldo, recuperación.',
    icon: ShieldCheck,
    to: ROUTES.PROFILE + '?tab=mfa',
    variant: 'success',
  },
  {
    title: 'Sesiones activas',
    description: 'Dispositivos en los que estás conectado.',
    icon: Monitor,
    to: ROUTES.PROFILE + '?tab=sessions',
    variant: 'info',
  },
  {
    title: 'Privacidad y datos',
    description: 'Habeas Data, descarga tus datos, gestiona consentimientos.',
    icon: FileLock2,
    to: ROUTES.PROFILE + '?tab=privacy',
    variant: 'warning',
  },
  {
    title: 'Cookies',
    description: 'Política de cookies y preferencias.',
    icon: Cookie,
    to: '/legal/cookies',
    variant: 'amber',
  },
]

// Cards de team (visibles si OWNER/ADMIN — placeholder por ahora).
const TEAM_CARDS: AccountCard[] = [
  {
    title: 'Equipo',
    description: 'Miembros de la empresa actual y sus roles.',
    icon: Users,
    to: ROUTES.PROFILE + '?tab=organizations',
    variant: 'brand',
  },
  {
    title: 'Invitar miembros',
    description: 'Próximamente: invita gente a tu empresa.',
    icon: UserPlus,
    to: '#',
    variant: 'success',
  },
]

// Atajos al panel de operación.
const OPERATION_LINK = {
  title: 'Ir al panel de operación',
  description: 'Volver al dashboard donde se gestiona la facturación electrónica.',
  to: ROUTES.DASHBOARD,
}

// ─── Page ──────────────────────────────────────────────────────────

export function AccountHubPage() {
  const { data: me, isLoading } = useCurrentUser()
  const logout = useLogout()
  const navigate = useNavigate()

  const isOwnerOrAdmin = me?.active_org?.role === 'OWNER' || me?.active_org?.role === 'ADMIN'

  return (
    <div className="min-h-screen bg-bg-app">
      {/* ─── Topbar (sticky) ───────────────────────────────────── */}
      <header className="sticky top-0 z-30 border-b border-border bg-bg-surface/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <a
            href={import.meta.env.VITE_APP_URL ?? 'https://taxor.co'}
            className="flex items-center gap-2"
          >
            <div
              aria-hidden
              className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-brand-500 to-brand-600 font-mono text-sm font-bold text-white shadow-sm"
            >
              Tx
            </div>
            <div>
              <p className="text-sm font-semibold leading-tight text-fg-primary">
                {APP.name}
              </p>
              <p className="text-[10px] uppercase tracking-wider text-fg-muted">
                Mi cuenta
              </p>
            </div>
          </a>
          <div className="flex items-center gap-2">
            {me && (
              <span className="hidden truncate text-xs text-fg-muted sm:inline">
                {me.email}
              </span>
            )}
            <ThemeToggle />
            <button
              type="button"
              onClick={async () => {
                await logout()
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-app px-3 py-1.5 text-xs font-medium text-fg-secondary transition-colors hover:bg-bg-elevated hover:text-fg-primary"
            >
              <LogOut className="h-3.5 w-3.5" />
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-10 px-4 py-10 sm:px-6">
        {/* ─── Welcome ──────────────────────────────────────────── */}
        <section>
          {isLoading || !me ? (
            <div className="flex items-center gap-3 text-fg-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Cargando…</span>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold tracking-tight text-fg-primary sm:text-3xl">
                Hola, <span className="bg-gradient-to-br from-brand-700 to-brand-500 bg-clip-text text-transparent">
                  {me.first_name || me.email}
                </span>
              </h1>
              <p className="mt-2 text-sm text-fg-secondary">
                Esta es tu cuenta de Taxor. Aquí gestionas tu identidad,
                seguridad, empresas y privacidad.
              </p>

              {me.active_org && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm">
                  <Building2 className="h-4 w-4 text-fg-muted" />
                  <span className="font-medium text-fg-primary">
                    {me.active_org.razon_social}
                  </span>
                  <span className="rounded-full bg-brand-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-brand-600">
                    {me.active_org.role}
                  </span>
                  {me.memberships_count > 1 && (
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.SELECT_ORG)}
                      className="ml-1 text-xs text-brand-600 hover:underline"
                    >
                      cambiar →
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </section>

        {/* ─── Cards: Identidad y seguridad ────────────────────── */}
        <Section title="Identidad y seguridad">
          <CardGrid cards={PROFILE_CARDS} />
        </Section>

        {/* ─── Cards: Equipo (si OWNER/ADMIN) ──────────────────── */}
        {isOwnerOrAdmin && (
          <Section
            title="Equipo y permisos"
            subtitle="Solo OWNER y ADMIN ven esta sección."
          >
            <CardGrid cards={TEAM_CARDS} />
          </Section>
        )}

        {/* ─── Atajo a operación ───────────────────────────────── */}
        <Section title="Operación">
          <button
            type="button"
            onClick={() => navigate(OPERATION_LINK.to)}
            className="group flex w-full items-center gap-4 rounded-xl border border-border bg-bg-surface p-4 text-left transition-all hover:-translate-y-0.5 hover:border-brand-500/40 hover:shadow-md"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-rose-500 text-white">
              <ExternalLink className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-fg-primary">
                {OPERATION_LINK.title}
              </p>
              <p className="text-xs text-fg-muted">{OPERATION_LINK.description}</p>
            </div>
            <ChevronRight className="h-4 w-4 text-fg-muted transition-transform group-hover:translate-x-0.5" />
          </button>
        </Section>

        {/* ─── Footer ──────────────────────────────────────────── */}
        <footer className="border-t border-border pt-6 text-center text-xs text-fg-muted">
          <p>
            Taxor · operado por <strong className="text-fg-secondary">Sociedad Colombiana de Consultoría</strong>{' '}
            · NIT 900.380.987-8
          </p>
          <p className="mt-1">
            ¿Necesitas ayuda? <a href="mailto:contacto@taxor.co" className="text-brand-600 hover:underline">contacto@taxor.co</a>
          </p>
        </footer>
      </main>
    </div>
  )
}

// ─── Subcomponents ────────────────────────────────────────────────

function Section({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: ReactNode
}) {
  return (
    <section>
      <div className="mb-4">
        <h2 className="text-base font-semibold text-fg-primary">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-fg-muted">{subtitle}</p>}
      </div>
      {children}
    </section>
  )
}

function CardGrid({ cards }: { cards: AccountCard[] }) {
  const navigate = useNavigate()
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {cards.map((c) => (
        <Card key={c.title} card={c} onClick={() => navigate(c.to)} />
      ))}
    </div>
  )
}

function Card({ card, onClick }: { card: AccountCard; onClick: () => void }) {
  const variantClasses = VARIANT_TINTS[card.variant]
  const Icon = card.icon
  const isExternal = card.to.startsWith('http')
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-start gap-3 rounded-xl border border-border bg-bg-surface p-4 text-left transition-all hover:-translate-y-0.5 hover:border-brand-500/30 hover:shadow-md"
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${variantClasses}`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-fg-primary">{card.title}</p>
        <p className="mt-0.5 line-clamp-2 text-xs text-fg-muted">{card.description}</p>
      </div>
      {isExternal ? (
        <ExternalLink className="mt-1 h-3.5 w-3.5 shrink-0 text-fg-muted transition-transform group-hover:translate-x-0.5" />
      ) : (
        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-fg-muted transition-transform group-hover:translate-x-0.5" />
      )}
    </button>
  )
}

const VARIANT_TINTS: Record<AccountCard['variant'], string> = {
  brand: 'bg-brand-500/10 text-brand-600',
  success: 'bg-success-tint text-success-fg',
  warning: 'bg-warning-tint text-warning-fg',
  info: 'bg-bg-elevated text-fg-secondary',
  amber: 'bg-warning-tint text-warning-fg',
  rose: 'bg-rose-500/10 text-rose-600',
}
